import { fetchUserInfo, journeyInit, type TenantConfig } from '@dmp/api';
import type { CachedUserInfo, InternalAppsPermission } from '@dmp/types/auth.types';
import { cacheJourneyId, getCachedJourneyId, getCachedUserInfo, getIsLoggedIn, getIsSsoUser } from '@dmp/utils';
import { type Environment, getEnv, getInternalAppsPermission } from '@dmp/utils/rbac';
import { DEFAULT_TENANT_CONFIG, fetchTenantConfig, isTenantEnvironment } from '@dmp/utils/tenant';
import type { AxiosError } from 'axios';
import { create } from 'zustand';

type GlobalStore = {
  journey_id: string | null;
  userInfo: CachedUserInfo | null;
  isLoading: boolean;
  isLoggedIn: boolean;
  isFetchingTenantConfig: boolean;
  isSsoUser: boolean;
  env: Environment;
  internalAppsPermission: InternalAppsPermission;
  tenantConfig: TenantConfig;
  tenantError: AxiosError | null;
  isTenant?: boolean;
};

export const useGlobalStore = create<GlobalStore>(() => ({
  journey_id: null,
  userInfo: getCachedUserInfo(),
  isLoading: false,
  isLoggedIn: getIsLoggedIn(),
  isFetchingTenantConfig: false,
  isSsoUser: getIsSsoUser(),
  env: getEnv(),
  internalAppsPermission: getInternalAppsPermission(),
  tenantConfig: DEFAULT_TENANT_CONFIG,
  tenantError: null,
  isTenant: isTenantEnvironment(),
}));

export const setJourneyId = (journey_id: string | null) => {
  useGlobalStore.setState({ journey_id });
};

export const setUserInfo = (userInfo: CachedUserInfo | null) => useGlobalStore.setState({ userInfo });

export const setIsLoading = (isLoading: boolean) => useGlobalStore.setState({ isLoading });

export const setIsLoggedIn = (isLoggedIn: boolean) => useGlobalStore.setState({ isLoggedIn });

export const setIsSsoUser = (isSsoUser: boolean) => useGlobalStore.setState({ isSsoUser });

export const setInternalAppsPermission = (internalAppsPermission: InternalAppsPermission) =>
  useGlobalStore.setState({ internalAppsPermission });

export const setTenantConfig = (tenantConfig: GlobalStore['tenantConfig']) => useGlobalStore.setState({ tenantConfig });

export const setIsFetchingTenantConfig = (isFetchingTenantConfig: boolean) =>
  useGlobalStore.setState({ isFetchingTenantConfig });

export const setTenantError = (tenantError: GlobalStore['tenantError']) => {
  useGlobalStore.setState({ tenantError });
};

export const getUserId = () => {
  return useGlobalStore.getState().userInfo?.login_uid;
};

export const getUserEmail = () => {
  return useGlobalStore.getState().userInfo?.user_email;
};

export const getTenantConfig = () => {
  return useGlobalStore.getState().tenantConfig;
};

function getInitialJourneyId() {
  // If current window has cached journey id use it, otherwise call journeyInit api to create a new journey.
  const cachedJourneyId = getCachedJourneyId();
  setJourneyId(cachedJourneyId);

  if (!cachedJourneyId && getIsLoggedIn()) {
    journeyInit()
      .then((res) => {
        setJourneyId(res.journey_id);
        // Also cache the journey id in session storage, so that if user refreshes the page, we can reuse it.
        cacheJourneyId(res.journey_id);
      })
      .catch(() => {
        // Journey tracking is best-effort; ignore failures (e.g. missing analytics access) same as other call sites.
      });
  }
}

export const fetchUserDetail = async () => {
  const cachedUserInfo = getCachedUserInfo();
  if (!cachedUserInfo) return;

  try {
    const res = await fetchUserInfo(cachedUserInfo.login_uid);
    setUserInfo({
      ...cachedUserInfo,
      ...{
        first_name: res.data.first_name,
        last_name: res.data.last_name,
        user_email: res.data.email,
        company: res.data.company,
      },
    });
  } catch {
    // do nothing
  }
};

export const pureFetchTenantConfig = async () => {
  // fetchTenantConfig already handled error.
  const res = await fetchTenantConfig();
  setTenantConfig(res);
};

export const launchRefreshTenantConfigTimer = () => {
  window.setInterval(
    () => {
      pureFetchTenantConfig();
    },
    // refresh tenant config every 30 mins
    1000 * 60 * 30
  );
};

export const initTenantConfig = async () => {
  setIsFetchingTenantConfig(true);
  try {
    await pureFetchTenantConfig();
  } catch (err) {
    console.error(err);
    setTenantError(err as AxiosError);
  } finally {
    setIsFetchingTenantConfig(false);
  }

  launchRefreshTenantConfigTimer();
};

export const initGlobalStore = async () => {
  initTenantConfig();
  setUserInfo(getCachedUserInfo());
  setIsLoggedIn(getIsLoggedIn());
  setIsSsoUser(getIsSsoUser());
  getInitialJourneyId();
  setInternalAppsPermission(getInternalAppsPermission());
};
